import { EditOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { isEqual, omit } from 'lodash';
import { useState } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';

import { EditBorrowerDetailsDocument, PortalSelfDocument } from '@willow/graphql-iso/src/portal';
import {
  addressToString,
  formatGqlDayAndTimeRange,
  phoneNumberFormat,
  stripObjectNullFromNullOrUndefined,
} from '@willow/shared-iso';
import {
  Button,
  ControlledCountrySelect,
  ControlledInput,
  ControlledPreferredContactTimes,
  ControlledStateSelect,
  DayAndTimeRange,
  getAddressInput,
  getDefaultAddress,
  OptionalValidAddress,
  PORTAL_LOAN_PATHS,
  Sentry,
  useZodHookForm,
  z,
} from '@willow/shared-web';
import { CompanyId, isUSCountryCode, OptionalPhoneNumber } from '@willow/types-iso';

import { PortalSelectedLoan } from '../../App';
import { EmptyPortalUser, PortalUser } from '../context/UserContext';

interface Props {
  companyId: CompanyId;
  user: PortalUser | EmptyPortalUser;
  loan: PortalSelectedLoan;
  manageMailingAddress: boolean;
}

const FormShape = z.object({
  phoneHome: OptionalPhoneNumber,
  phoneWork: OptionalPhoneNumber,
  phoneCell: OptionalPhoneNumber,
  mailingAddress: OptionalValidAddress,
  preferredContactTimes: z.array(DayAndTimeRange).optional(),
});
type TFormShape = z.infer<typeof FormShape>;

export const AccountInformation = ({ user, companyId, loan, manageMailingAddress }: Props) => {
  const [isEditForm, setIsEditForm] = useState(false);
  const [editBorrowerDetails] = useMutation(EditBorrowerDetailsDocument, {
    refetchQueries: [PortalSelfDocument],
  });
  const borrowerUser = [loan.primaryBorrower, ...loan.additionalBorrowers].find((b) => b.id === user.borrowerId);

  const DEFAULT_VALUES: TFormShape = {
    phoneHome: borrowerUser?.phoneNumbers.home ? phoneNumberFormat(borrowerUser.phoneNumbers.home) : undefined,
    phoneWork: borrowerUser?.phoneNumbers.work ? phoneNumberFormat(borrowerUser.phoneNumbers.work) : undefined,
    phoneCell: borrowerUser?.phoneNumbers.cell ? phoneNumberFormat(borrowerUser.phoneNumbers.cell) : undefined,
    mailingAddress: borrowerUser?.mailingAddress
      ? stripObjectNullFromNullOrUndefined(borrowerUser.mailingAddress)
      : getDefaultAddress(),
    preferredContactTimes: borrowerUser?.preferredContactTimes.map((pct) => omit(pct, '__typename')),
  };

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useZodHookForm(FormShape, {
    defaultValues: DEFAULT_VALUES,
  });

  if (!borrowerUser) {
    Sentry.captureException(
      `User's borrowerId does not exist on loan. borrowerId=${user.borrowerId}, loanId=${loan.id}, company=${loan.company.id}`,
    );
    return <></>;
  }
  const preferredContactTimes = watch('preferredContactTimes');
  const mailingAddressCountry = watch('mailingAddress.country');

  const onSubmit = handleSubmit(async (data) => {
    // Note: isDirty isn't handling deep comparisons
    const hasContactTimeChanges = !isEqual(
      data.preferredContactTimes,
      borrowerUser.preferredContactTimes.map((pct) => omit(pct, '__typename')),
    );

    if (!isDirty && !hasContactTimeChanges) {
      toast.error('No changes detected to your account information. Please try again');
      return;
    }

    try {
      await editBorrowerDetails({
        variables: {
          companyId,
          loanId: loan.id,
          input: {
            ...data,
            mailingAddress: getAddressInput(data.mailingAddress),
          },
        },
      });

      toast.success('Your account information has been successfully updated.');
      setIsEditForm(false);
      reset({
        phoneHome: data.phoneHome,
        phoneCell: data.phoneCell,
        phoneWork: data.phoneWork,
        mailingAddress: data.mailingAddress,
        preferredContactTimes: data.preferredContactTimes,
      });
    } catch (_err: unknown) {
      toast.error('Something went wrong updating your account information, please try again.');
    }
  });

  return (
    <>
      <div className={classNames({ 'u-bg-bark00': isEditForm })}>
        <div className="mb-2 u-border-1 u-border-color-bark1 u-border-r-3">
          <div className="px-4 py-3 d-flex align-items-center justify-content-between u-border-b-1 u-border-color-bark1">
            <h3 className="fw-bold">Full Name</h3>
            <p className="u-color-bark3">
              {user.firstName} {user.lastName}
              {user.suffix ? ` ${user.suffix}` : ''}
            </p>
          </div>

          <div className="px-4 py-3 d-flex align-items-center justify-content-between u-border-b-1 u-border-color-bark1">
            <h3 className="fw-bold">Email Address</h3>
            <p className="u-color-bark3">{user.email}</p>
          </div>

          {isEditForm && manageMailingAddress ? (
            <div className="px-4 py-3 u-border-b-1 u-border-color-bark1">
              <h3 className="fw-bold">Mailing Address</h3>
              <div>
                <ControlledCountrySelect
                  control={control}
                  fieldName="mailingAddress.country"
                  placeholder="Country"
                  label="Country"
                />
              </div>
              <div className="mt-2">
                <ControlledInput
                  control={control}
                  fieldName="mailingAddress.line1"
                  placeholder="Mailing street address line 1"
                  label="Street address line 1"
                />
              </div>
              <div className="mt-2">
                <div>
                  <ControlledInput
                    control={control}
                    fieldName="mailingAddress.line2"
                    placeholder="Mailing street address line 2"
                    label="Street address line 2"
                  />
                </div>
              </div>
              <div className="mt-2">
                <ControlledInput
                  control={control}
                  fieldName="mailingAddress.line3"
                  placeholder="Mailing street address line 3"
                  label="Street address line 3"
                />
              </div>
              <div className="mt-2">
                <ControlledInput
                  control={control}
                  fieldName="mailingAddress.line4"
                  placeholder="Mailing street address line 4"
                  label="Street address line 4"
                />
              </div>
              <div className="mt-2">
                <ControlledInput
                  control={control}
                  fieldName="mailingAddress.locality"
                  placeholder="Mailing address city"
                  label="City"
                />
              </div>

              <div className="mt-2 d-flex gap-2">
                <div className="w-50">
                  {!mailingAddressCountry || isUSCountryCode(mailingAddressCountry) ? (
                    <ControlledStateSelect
                      control={control}
                      fieldName="mailingAddress.region"
                      label="State"
                      placeholder="State"
                    />
                  ) : (
                    <ControlledInput
                      control={control}
                      fieldName="mailingAddress.region"
                      label="Region"
                      placeholder="Region"
                    />
                  )}
                </div>
                <div className="w-50">
                  <ControlledInput
                    control={control}
                    fieldName="mailingAddress.postcode"
                    label={
                      !mailingAddressCountry || isUSCountryCode(mailingAddressCountry) ? 'Zip code' : 'Postal code'
                    }
                    placeholder={
                      !mailingAddressCountry || isUSCountryCode(mailingAddressCountry) ? 'Zip code' : 'Postal code'
                    }
                  />
                </div>
              </div>
              {errors?.mailingAddress?.message && (
                <div className="u-fs-2 u-color-red1 mt-2">{errors.mailingAddress.message}</div>
              )}
            </div>
          ) : (
            <div className="px-4 py-3 d-flex align-items-center justify-content-between u-border-b-1 u-border-color-bark1">
              <h3 className="fw-bold">Mailing Address</h3>
              <p className="u-color-bark3">
                {borrowerUser.mailingAddress && addressToString(borrowerUser.mailingAddress)}
              </p>
            </div>
          )}

          <div className="px-4 py-3 d-flex align-items-center justify-content-between u-border-b-1 u-border-color-bark1">
            <h3 className="fw-bold">Phone (home)</h3>
            {isEditForm ? (
              <div>
                <ControlledInput fieldName="phoneHome" control={control} placeholder="(555) 555-5555" />
              </div>
            ) : (
              <p className="u-color-bark3">
                {borrowerUser.phoneNumbers.home && phoneNumberFormat(borrowerUser.phoneNumbers.home)}
              </p>
            )}
          </div>
          <div className="px-4 py-3 d-flex align-items-center justify-content-between u-border-b-1 u-border-color-bark1">
            <h3 className="fw-bold">Phone (cell)</h3>
            {isEditForm ? (
              <div>
                <ControlledInput fieldName="phoneCell" control={control} placeholder="(555) 555-5555" />
              </div>
            ) : (
              <p className="u-color-bark3">
                {borrowerUser.phoneNumbers.cell && phoneNumberFormat(borrowerUser.phoneNumbers.cell)}
              </p>
            )}
          </div>
          <div className="px-4 py-3 d-flex align-items-center justify-content-between u-border-b-1 u-border-color-bark1">
            <h3 className="fw-bold">Phone (work)</h3>
            {isEditForm ? (
              <div>
                <ControlledInput fieldName="phoneWork" control={control} placeholder="(555) 555-5555" />
              </div>
            ) : (
              <p className="u-color-bark3">
                {borrowerUser.phoneNumbers.work && phoneNumberFormat(borrowerUser.phoneNumbers.work)}
              </p>
            )}
          </div>

          <div
            className={classNames('px-4 py-3', {
              'd-flex align-items-top justify-content-between': !isEditForm,
            })}
          >
            <h3 className="fw-bold">Preferred Contact Time</h3>

            {isEditForm ? (
              <div className="mt-3">
                <ControlledPreferredContactTimes
                  control={control as any as Control<Pick<TFormShape, 'preferredContactTimes'>>}
                  preferredContactTimes={preferredContactTimes}
                  setValue={setValue as any as UseFormSetValue<Pick<TFormShape, 'preferredContactTimes'>>}
                  errors={errors}
                />
              </div>
            ) : (
              <div>
                {borrowerUser.preferredContactTimes?.map((t, index) => {
                  return (
                    <div className="u-color-bark3" key={`pt-${index}`}>
                      {formatGqlDayAndTimeRange(t)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-3">
        {isEditForm ? (
          <>
            {!isSubmitting && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="me-2"
                onClick={() => {
                  setIsEditForm(false);
                  reset();
                }}
              >
                Cancel
              </Button>
            )}

            <Button type="submit" variant="primary" size="sm" onClick={onSubmit} loading={isSubmitting}>
              Save
            </Button>
          </>
        ) : (
          <Button type="button" variant="secondary" size="sm" onClick={() => setIsEditForm(true)}>
            <EditOutlined className="me-2" /> Edit
          </Button>
        )}
      </div>

      <div className="mt-5 mx-auto text-center" style={{ maxWidth: '300px' }}>
        If your account information has changed
        <br />
        <NavLink
          to={PORTAL_LOAN_PATHS.manageLoan.contactUs({ losId: loan.losId, companyId })}
          exact={false}
          className="u-color-blue1 u-bold"
        >
          contact us for assistance.
        </NavLink>
      </div>
    </>
  );
};
